import { useEffect, useMemo, useState } from 'react';
import type { RuntimeNavigationGraph, RuntimeSurface } from '@boox/contracts';
import {
  type DynamicRuntimeBinding,
} from '@/lib/dynamic-runtime-bindings';
import {
  compareRuntimeNavigation,
  getRuntimeNavigation,
  getRuntimeNavigationBadges,
  recordRuntimeNavigationNodeClick,
  resolvedDynamicRuntimeBindingForPath,
} from '@/lib/runtime-api';

type DynamicNavigationStatus = 'idle' | 'loading' | 'ready' | 'error';
type DynamicNavigationCompareStatus = 'idle' | 'loading' | 'ready' | 'error';

export type DynamicNavigationModule = string;

export type DynamicNavigationBadge = {
  ref: string;
  value: number;
  tone?: 'neutral' | 'info' | 'warning' | 'danger' | 'success';
  label?: string;
  hidden?: boolean;
  updatedAt?: string;
  metadata?: Record<string, unknown>;
};

export type DynamicNavigationVisibleItem = {
  id: string;
  route: string;
  label?: string;
  categoryId?: string;
};

export type DynamicNavigationCompare = {
  status: 'matched' | 'mismatch';
  counts: {
    legacyVisible: number;
    dynamicVisible: number;
    matched: number;
    legacyOnly: number;
    dynamicOnly: number;
    hidden: number;
    mismatch: number;
  };
  legacyOnly: DynamicNavigationVisibleItem[];
  dynamicOnly: Array<{
    id: string;
    route: string;
    label: string;
    group?: string;
    requiredPermissions: string[];
  }>;
  hiddenNodes: RuntimeNavigationGraph['hiddenNodes'];
  matchedRoutes: string[];
};

export type DynamicNavigationNodeClick = DynamicNavigationVisibleItem & {
  activeRoute?: string;
};

export function inferDynamicNavigationBinding(pathname: string): DynamicRuntimeBinding | undefined {
  return resolvedDynamicRuntimeBindingForPath(pathname);
}

export function inferDynamicNavigationModule(pathname: string): DynamicNavigationModule | undefined {
  return inferDynamicNavigationBinding(pathname)?.module;
}

function dynamicNavigationQuery(params: {
  surface: RuntimeSurface;
  module: DynamicNavigationModule;
  route: string;
  profileTemplateCode?: string;
  locale?: string;
}) {
  return new URLSearchParams(
    Object.entries(params)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}

export function recordDynamicNavigationNodeClick(params: {
  surface?: RuntimeSurface;
  module?: DynamicNavigationModule;
  profileTemplateCode?: string;
  locale?: string;
  route: string;
  click: DynamicNavigationNodeClick;
}) {
  const surface = params.surface ?? 'web';
  if (!params.module) return;
  const qs = dynamicNavigationQuery({
    surface,
    module: params.module,
    profileTemplateCode: params.profileTemplateCode,
    locale: params.locale,
    route: params.route,
  });
  recordRuntimeNavigationNodeClick(qs, params.click)
    .catch(() => undefined);
}

export function useDynamicNavigationGraph(params: {
  surface?: RuntimeSurface;
  module?: DynamicNavigationModule;
  profileTemplateCode?: string;
  locale?: string;
  route: string;
  refreshKey?: string;
  visibleItems?: DynamicNavigationVisibleItem[];
  enabled?: boolean;
}) {
  const surface = params.surface ?? 'web';
  const enabled = params.enabled ?? true;
  const [graph, setGraph] = useState<RuntimeNavigationGraph | null>(null);
  const [status, setStatus] = useState<DynamicNavigationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [badges, setBadges] = useState<Record<string, DynamicNavigationBadge>>({});
  const [compare, setCompare] = useState<DynamicNavigationCompare | null>(null);
  const [compareStatus, setCompareStatus] = useState<DynamicNavigationCompareStatus>('idle');
  const [compareError, setCompareError] = useState<string | null>(null);
  const visibleItemsKey = useMemo(
    () => JSON.stringify(params.visibleItems ?? []),
    [params.visibleItems],
  );
  const refreshKey = params.refreshKey ?? '';

  const query = useMemo(() => {
    if (!enabled || !params.module) return null;
    return {
      surface,
      module: params.module,
      profileTemplateCode: params.profileTemplateCode,
      locale: params.locale,
      route: params.route,
    };
  }, [enabled, params.locale, params.module, params.profileTemplateCode, params.route, surface]);

  useEffect(() => {
    let cancelled = false;
    if (!query) {
      setGraph(null);
      setBadges({});
      setStatus('idle');
      setError(null);
      return;
    }

    setStatus('loading');
    setError(null);
    getRuntimeNavigation(query)
      .then((nextGraph) => {
        if (cancelled) return;
        setGraph(nextGraph);
        setStatus('ready');
        if (nextGraph.nodes.some((node) => node.badgeRef)) {
          getRuntimeNavigationBadges(query)
            .then((result) => {
              if (!cancelled) setBadges(result.badges as Record<string, DynamicNavigationBadge>);
            })
            .catch(() => {
              if (!cancelled) setBadges({});
            });
        } else {
          setBadges({});
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setGraph(null);
        setBadges({});
        setStatus('error');
        setError(err instanceof Error ? err.message : 'dynamic_navigation_unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [query, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    if (!query) {
      setCompare(null);
      setCompareStatus('idle');
      setCompareError(null);
      return;
    }

    const visibleItems = JSON.parse(visibleItemsKey) as DynamicNavigationVisibleItem[];
    if (visibleItems.length === 0) {
      setCompare(null);
      setCompareStatus('idle');
      setCompareError(null);
      return;
    }

    const qs = dynamicNavigationQuery(query);
    setCompareStatus('loading');
    setCompareError(null);

    compareRuntimeNavigation(qs, { visibleItems })
      .then((result) => {
        if (cancelled) return;
        setCompare(result.compare as DynamicNavigationCompare);
        setCompareStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setCompare(null);
        setCompareStatus('error');
        setCompareError(err instanceof Error ? err.message : 'dynamic_navigation_compare_unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [query, refreshKey, visibleItemsKey]);

  return {
    graph,
    badges,
    status,
    error,
    compare,
    compareStatus,
    compareError,
    mismatchCount: compare?.counts.mismatch ?? 0,
    hiddenCount: graph?.hiddenNodes.length ?? 0,
    visibleCount: graph?.nodes.length ?? 0,
  };
}
